import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";

// Comparación sin mayúsculas, tildes ni espacios de más
const norm = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * Buscador de productos con autocompletado (nombre, código, SKU o marca). Flechas + Enter o clic para elegir.
 * Muestra nombre y código para distinguir productos parecidos. `onPick(id, producto)`.
 * Compartido por Nueva factura / proforma y por la factura de proveedor (Compras).
 */
export default function ProductSearch({ products, value, onPick, testid, placeholder, autoFocus = false, mostrarStock = true }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const wrapRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({ display: "none" });
  const selected = products.find((p) => p.id === value) || null;

  const filtered = useMemo(() => {
    if (!q.trim()) return products.slice(0, 25);
    const s = norm(q);
    return products
      .filter((p) => [p.nombre, p.codigo, p.sku, p.marca].some((v) => norm(v).includes(s)))
      .slice(0, 30);
  }, [q, products]);

  // Reposiciona el dropdown (fixed) para que no lo clipee el overflow del contenedor
  useEffect(() => {
    if (!open || !wrapRef.current) { setDropdownStyle({ display: "none" }); return; }
    const rect = wrapRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 360),
      maxHeight: 320,
      zIndex: 60,
    });
  }, [open, q, filtered.length]);

  const pick = (p) => {
    onPick(p.id, p);
    setQ(""); setOpen(false);
  };

  if (selected && !open) {
    return (
      <div className="flex items-center gap-1.5 bg-agro-green/10 border border-agro-green/40 rounded px-2 py-1.5" data-testid={testid}>
        <span className="font-mono text-[10px] text-agro-green">{selected.codigo}</span>
        <span className="text-xs text-text-primary flex-1 truncate">{selected.nombre}</span>
        <button type="button" onClick={() => { setOpen(true); setQ(""); }}
                data-testid={`${testid}-change`}
                className="text-[10px] text-text-muted hover:text-agro-yellow underline">cambiar</button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="flex items-center bg-app border border-border rounded focus-within:border-agro-green">
        <MagnifyingGlassIcon size={12} className="text-text-muted ml-2" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHover(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter" && filtered[hover]) { e.preventDefault(); pick(filtered[hover]); }
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder || "Buscar por nombre, código, SKU o marca…"}
          autoFocus={autoFocus}
          data-testid={`${testid}-input`}
          className="w-full bg-transparent px-2 py-1.5 text-xs focus:outline-none"
        />
        {q && (
          <button aria-label="Limpiar" type="button" onClick={() => setQ("")} className="text-text-muted hover:text-text-primary px-2">
            <XIcon size={12} />
          </button>
        )}
      </div>
      {open && (
        <div style={dropdownStyle}
             className="bg-card border border-border rounded-md shadow-xl overflow-y-auto"
             data-testid={`${testid}-list`}
             onMouseDown={(e) => e.preventDefault()}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-text-muted">No hay productos que coincidan</div>
          ) : filtered.map((p, k) => (
            <button
              type="button"
              key={p.id}
              onClick={() => pick(p)}
              onMouseEnter={() => setHover(k)}
              data-testid={`${testid}-opt-${p.codigo}`}
              className={`w-full text-left px-2 py-1.5 flex items-center gap-2 border-b border-border/40 last:border-0 ${k === hover ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
            >
              <span className="font-mono text-[10px] text-agro-green shrink-0">{p.codigo}</span>
              <span className="text-xs text-text-primary flex-1 truncate">{p.nombre}</span>
              {p.marca && <span className="text-[10px] text-text-muted shrink-0">{p.marca}</span>}
              {p.tipo === "servicio" ? <span className="text-[10px] text-agro-yellow shrink-0">servicio</span>
                : mostrarStock && <span className="text-[10px] text-text-muted shrink-0">stock {p.stock_actual}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
